import { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import type { BreadcrumbItem } from './Header';

/**
 * Ancestor breadcrumb trail for the current pathname. The page itself carries
 * the single visible title, so only the trail above it lives in the header.
 */
function resolveBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter((segment) => segment.length > 0);
  const [first, second, third] = segments;

  switch (first) {
    case 'students':
      if (second === 'new') return [{ label: 'Students', to: '/students' }];
      if (!second) return [];
      if (third === 'edit') {
        return [
          { label: 'Students', to: '/students' },
          { label: 'Student details', to: `/students/${second}` },
        ];
      }
      return [{ label: 'Students', to: '/students' }];
    case 'payments':
      return second === 'new' ? [{ label: 'Payments', to: '/payments' }] : [];
    case 'reports':
      return second ? [{ label: 'Reports', to: '/reports' }] : [];
    case 'accounts':
      return second ? [{ label: 'Student Accounts', to: '/accounts' }] : [];
    default:
      return [];
  }
}

/**
 * App shell: sticky dark steel sidebar, white header, pale blue workspace.
 * Provides the skip-to-content link and mounts page content via <Outlet/>.
 */
export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const breadcrumbs = useMemo(() => resolveBreadcrumbs(location.pathname), [location.pathname]);

  return (
    <div className="flex min-h-screen bg-steel-pale">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Header breadcrumbs={breadcrumbs} onOpenSidebar={() => setMobileOpen(true)} />
        <main id="main-content" tabIndex={-1} className="print-workspace flex-1 px-4 py-5 outline-none md:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
